import { captureAttribution, readAttributionSnapshot } from './attribution';
import { createEventId, createId } from './eventIds';
import {
  dumpQueuedEvents,
  enqueueEvent,
  flushEventQueue,
  initializeEventQueue,
} from './eventQueue';
import { initMetaPixel, isMetaPixelConfigured, trackMetaEvent } from './metaPixel';

const TRACKING_POLICY_VERSION = 'frontend-funnel-v1';
const CONSENT_STATUS = 'immediate_capture';
const pageStates = new Map();

function hasWindow() {
  return typeof window !== 'undefined';
}

function getViewport() {
  if (!hasWindow()) {
    return { width: 0, height: 0, device_pixel_ratio: 1 };
  }

  return {
    width: window.innerWidth,
    height: window.innerHeight,
    device_pixel_ratio: window.devicePixelRatio || 1,
  };
}

function getScroll(pageState) {
  if (!hasWindow()) {
    return { x: 0, y: 0, max_y: 0 };
  }

  const y = Math.max(0, window.scrollY || document.documentElement.scrollTop || 0);
  const x = Math.max(0, window.scrollX || document.documentElement.scrollLeft || 0);
  pageState.maxScrollY = Math.max(pageState.maxScrollY || 0, y);

  return {
    x,
    y,
    max_y: pageState.maxScrollY,
  };
}

function routeKeyFromLocation(location, variant) {
  return [
    location?.key || 'static',
    location?.pathname || window.location.pathname,
    location?.search || window.location.search,
    variant.key,
  ].join(':');
}

function getPageState(routeKey) {
  if (!pageStates.has(routeKey)) {
    pageStates.set(routeKey, {
      page_instance_id: createId('page_instance'),
      firedOnce: new Set(),
      maxScrollY: 0,
    });
  }

  return pageStates.get(routeKey);
}

function experimentFields(assignment) {
  if (!assignment) {
    return {};
  }

  return {
    experiment_key: assignment.experiment_key,
    experiment_variant: assignment.experiment_variant,
    assigned_visual_funnel: assignment.assigned_visual_funnel,
    assignment_id: assignment.assignment_id,
    assignment_source: assignment.assignment_source,
    assignment_created_at: assignment.assignment_created_at,
  };
}

function installDebugBridge(tracker) {
  if (!hasWindow()) {
    return;
  }

  window.__funnelTracking = {
    dump: async () => ({
      tracker: tracker.getContext(),
      attribution: readAttributionSnapshot(),
      queue: await dumpQueuedEvents(),
      meta_pixel_configured: isMetaPixelConfigured(),
    }),
    flush: (options) => tracker.flush(options),
    track: (eventName, payload) => tracker.track(eventName, payload),
  };
}

export function createFunnelTracker({ variant, location, experimentAssignment = null }) {
  const routeKey = routeKeyFromLocation(location, variant);
  const pageState = getPageState(routeKey);
  const attributionContext = captureAttribution({ variant });
  const experimentContext = experimentFields(experimentAssignment);

  initializeEventQueue();

  if (isMetaPixelConfigured()) {
    initMetaPixel();
  }

  const tracker = {
    getContext() {
      const latestTouch = attributionContext.attribution.latest_touch || {};

      return {
        visitor_session_id: attributionContext.visitor_session_id,
        anonymous_user_id: attributionContext.anonymous_user_id,
        page_instance_id: pageState.page_instance_id,
        funnel_variant: variant.key,
        z_source: latestTouch.z_source || 'direct',
        z_campaign: latestTouch.z_campaign || '',
        z_ad: latestTouch.z_ad || variant.zAd,
        route_path: hasWindow() ? window.location.pathname : location?.pathname || '',
        page_url: hasWindow() ? window.location.href : '',
        referrer: hasWindow() ? document.referrer || '' : '',
        user_agent: hasWindow() ? navigator.userAgent : '',
        meta: attributionContext.meta,
        attribution: attributionContext.attribution,
        consent_status: CONSENT_STATUS,
        tracking_policy_version: TRACKING_POLICY_VERSION,
        ...experimentContext,
      };
    },

    buildEvent(eventName, payload = {}, eventId) {
      const context = this.getContext();

      return {
        event_id: eventId || createEventId(eventName),
        event_name: eventName,
        event_time: Math.floor(Date.now() / 1000),
        ...context,
        viewport: getViewport(),
        scroll: getScroll(pageState),
        payload,
      };
    },

    async track(eventName, payload = {}, options = {}) {
      const onceKey = options.onceKey || '';

      if (onceKey && pageState.firedOnce.has(onceKey)) {
        return null;
      }

      if (onceKey) {
        pageState.firedOnce.add(onceKey);
      }

      const event = this.buildEvent(eventName, payload, options.eventId);
      await enqueueEvent(event);

      if (options.sendToMeta) {
        trackMetaEvent(eventName, event, event.event_id);
      }

      return event;
    },

    async trackInitialView() {
      await this.track(
        'landing_page_view',
        {
          title: document.title,
          source: 'funnel_mount',
        },
        { sendToMeta: true, onceKey: 'landing_page_view' }
      );

      await this.track(
        'funnel_view',
        {
          headline: variant.headline,
          content_loaded: true,
        },
        { sendToMeta: true, onceKey: 'funnel_view' }
      );

      await this.track(
        'funnel_variant_view',
        {
          variant_slug: variant.slug,
          z_ad: variant.zAd,
        },
        { sendToMeta: true, onceKey: 'funnel_variant_view' }
      );
    },

    async trackExperimentAssignment() {
      if (!experimentAssignment) {
        return null;
      }

      return this.track('experiment_assignment', experimentContext, {
        onceKey: `experiment_assignment:${experimentAssignment.assignment_id}`,
      });
    },

    async trackTelegramClick(payload = {}) {
      const eventId = createEventId('telegram_cta_click');
      return this.track(
        'telegram_cta_click',
        {
          destination: variant.telegramUrl || 'https://t.me/cryptoxnfts',
          ...payload,
        },
        {
          eventId,
          sendToMeta: true,
        }
      );
    },

    flush(options) {
      return flushEventQueue(options);
    },
  };

  installDebugBridge(tracker);
  return tracker;
}
